import React, { useMemo, useRef } from 'react';
import TerminalSendEmailForm, { TerminalSendEmailFormRef } from './TerminalSendEmailForm';
import type { EmailOption } from './TerminalChipInput';
import { PanelEmailData } from '../../mail/contexts/EmailPanelContext';
import { FeedEmailData } from '../../mail/components/EmailFeedContent';
import { ComposeMode } from './TerminalWorkspaceRail';

/**
 * TerminalComposer — terminal-spirit email composer wrapper.
 *
 * Mounts TerminalSendEmailForm (purpose-built for the terminal theme)
 * inside a frame that provides the mode label + [↗ expand] / [discard]
 * / [×] header actions. The classic SendEmailForm is NOT used here —
 * the terminal form is a separate implementation that shares the
 * backend (same draft Firestore collection, same send cloud function,
 * same EmailPanelContext) but has no Bootstrap chrome to fight.
 *
 * Two render variants, same internals:
 *   - variant="inline" — embedded under an email entry in the thread log
 *   - variant="pane"   — fills a pane slot in the adaptive two-column layout
 */

export interface TerminalComposerProps {
  variant: 'inline' | 'pane';
  mode: ComposeMode;
  /** The email being replied to / forwarded. Required for reply/replyAll/forward. */
  sourceEmail?: FeedEmailData | null;
  /** For new-email: the recipient's address (prefills the To: field). */
  defaultTo?: string;
  /** Pane resumed from / promoted with this saved draft: the form hydrates it over the
   *  reply / forward template instead of keeping the template's prefilled recipients. */
  resumeDraftId?: string | null;
  /** Job link — any email sent from here inherits linkage to this job. */
  linkedJobId: string;
  linkedJobLabel?: string;
  /** Emails to surface at the top of the recipient autocomplete
   *  (job's customer primary + their sub-contacts). Host-ranked. */
  priorityEmails?: EmailOption[];
  /** Called when the send completes successfully. Used for the log-line flash. */
  onSendSuccess: (ctx: { subject: string; mode: ComposeMode }) => void;
  /** Called when the user explicitly discards (deletes the draft + closes). */
  onDiscard: () => void;
  /** Called when the user closes WITHOUT discarding (draft auto-save keeps the content). */
  onClose: () => void;
  /** Inline-only: promotes the composer into a pane slot. TerminalComposer
   *  awaits the in-flight auto-save + reads the current draft id from its
   *  internal SendEmailForm ref BEFORE calling this, so the caller can
   *  hydrate the new pane from that draft instead of starting over with a
   *  bare reply template (previously lost any typed body across the
   *  transition). */
  onExpandToPane?: (ctx: { draftId: string | null }) => void;
}

/** Email-doc fields the composer reads (via replyRecipients) that FeedEmailData may not type yet. */
interface ReplySignals {
  emailType?: unknown;
  direction?: unknown;
  senderEmail?: string;
  deleted?: unknown;
  quarantined?: unknown;
}

function modeLabel(m: ComposeMode): string {
  switch (m) {
    case 'reply':    return 'drafting reply';
    case 'replyAll': return 'drafting reply-all';
    case 'forward':  return 'drafting forward';
    case 'new':      return 'new email';
  }
}

/** Shown when the composer is closed / promoted while a file is still uploading. */
export const UPLOAD_IN_FLIGHT_CONFIRM =
  'An attachment is still uploading - close anyway? The upload will be cancelled.';

const TerminalComposer: React.FC<TerminalComposerProps> = ({
  variant,
  mode,
  sourceEmail,
  defaultTo,
  resumeDraftId,
  linkedJobId,
  linkedJobLabel,
  priorityEmails,
  onSendSuccess,
  onDiscard,
  onClose,
  onExpandToPane,
}) => {
  const ref = useRef<TerminalSendEmailFormRef>(null);

  // Build the initialData blob for SendEmailForm. This is the same shape
  // the classic modal uses — isReply/isReplyAll/isForward flags drive the
  // form's internal prefill logic (To/Subject/quoted body).
  const initialData: PanelEmailData | null = useMemo(() => {
    if (mode === 'new') {
      // Fresh compose; To defaults to the customer's primary email if given.
      return {
        to: defaultTo || '',
        isReply: false,
        isForward: false,
        isReplyAll: false,
      };
    }
    if (!sourceEmail) {
      // Guard — caller should always pass sourceEmail for reply/forward modes.
      // Render an empty compose to avoid crashing.
      return { isReply: false, isForward: false };
    }
    // The signals the composer needs to tell "I sent this" from "a client sent
    // this" (replyRecipients): folder alone is not enough - 336 of 1,265 owner-
    // authored docs in the last 200 days sit in inbox/deleted/drafts with
    // direction 'in'. Widened locally so this file does not depend on how far
    // PanelEmailData has been extended with them.
    const src = sourceEmail as FeedEmailData & ReplySignals;
    const base: PanelEmailData & ReplySignals = {
      id: sourceEmail.id,
      to: sourceEmail.to,
      cc: sourceEmail.cc,
      bcc: sourceEmail.bcc,
      from: sourceEmail.from,
      subject: sourceEmail.subject,
      html: sourceEmail.html,
      text: sourceEmail.text,
      date: sourceEmail.date,
      attachments: sourceEmail.attachments,
      customerId: sourceEmail.customerId,
      source: sourceEmail.source,
      mailbox: sourceEmail.mailbox,
      graph: sourceEmail.graph,
      hasAttachments: sourceEmail.hasAttachments,
      folder: sourceEmail.folder,
      emailType: src.emailType,
      direction: src.direction,
      senderEmail: src.senderEmail,
      deleted: src.deleted,
      quarantined: src.quarantined,
    };
    if (mode === 'reply')    return { ...base, isReply: true,  isForward: false, isReplyAll: false };
    if (mode === 'replyAll') return { ...base, isReply: false, isForward: false, isReplyAll: true  };
    return                          { ...base, isReply: false, isForward: true,  isReplyAll: false };
  }, [mode, sourceEmail, defaultTo]);

  const subjectForFlash = useMemo(() => {
    if (mode === 'new') return 'New email';
    if (!sourceEmail?.subject) return '(no subject)';
    const subj = sourceEmail.subject.trim();
    if (mode === 'forward')  return subj.toLowerCase().startsWith('fwd:') ? subj : `Fwd: ${subj}`;
    return subj.toLowerCase().startsWith('re:') ? subj : `Re: ${subj}`;
  }, [mode, sourceEmail]);

  const handleDiscard = async () => {
    try {
      await ref.current?.prepareForDiscard();
    } catch {
      // prepareForDiscard is best-effort; proceed to close either way
    }
    onDiscard();
  };

  // Force the inline composer's in-flight auto-save to land, then pass
  // the resulting draft id up so the new pane can hydrate from it. This
  // is the fix for the promote-to-pane data-loss race: without this,
  // the inline form's unmount would fire-and-forget a save, the new
  // pane would mount with the bare reply template, and the user's
  // typed body would be left in a draft the pane never loads.
  //
  // An upload still in flight would be aborted by the inline form's unmount
  // (nothing ever references the half-finished object) and the draft would
  // autosave WITHOUT the file - so ask first, and stay put if declined.
  const confirmDiscardUpload = (): boolean =>
    !ref.current?.hasUploadInFlight || window.confirm(UPLOAD_IN_FLIGHT_CONFIRM);

  const handleExpand = async () => {
    if (!onExpandToPane) return;
    if (!confirmDiscardUpload()) return;
    try {
      await ref.current?.triggerSave();
    } catch {
      // Save is best-effort — promote either way so the user isn't blocked.
    }
    const draftId = ref.current?.currentDraftId ?? null;
    onExpandToPane({ draftId });
  };

  // Close without discarding: the draft autosaves on the way out, but an
  // in-flight upload does not survive the unmount.
  const handleClose = () => {
    if (!confirmDiscardUpload()) return;
    onClose();
  };

  const wrapperClass =
    'term-composer'
    + (variant === 'inline' ? ' term-composer--inline' : ' term-composer--pane');

  return (
    <div className={wrapperClass}>
      {/* ── Header bar ──────────────────────────────────────────────── */}
      <div className="term-composer__header">
        <span className="term-composer__header-rule">─── ▸ </span>
        <span className="term-composer__header-label">{modeLabel(mode)}</span>
        <span className="term-composer__header-rule term-composer__header-rule--fill" aria-hidden="true" />
        {variant === 'inline' && onExpandToPane && (
          <button
            type="button"
            className="term-composer__action"
            onClick={handleExpand}
            title="Promote this composer to its own column (auto-saves first so nothing is lost)"
          >
            <span className="term-composer__bracket">[</span>↗ expand<span className="term-composer__bracket">]</span>
          </button>
        )}
        <button
          type="button"
          className="term-composer__action term-composer__action--danger"
          onClick={handleDiscard}
          title="Discard draft (deletes the saved draft)"
        >
          <span className="term-composer__bracket">[</span>discard<span className="term-composer__bracket">]</span>
        </button>
        <button
          type="button"
          className="term-composer__action"
          onClick={handleClose}
          title="Close without discarding (your draft is auto-saved)"
          aria-label="Close composer"
        >
          [×]
        </button>
      </div>

      {/* ── Form body ───────────────────────────────────────────────── */}
      <div className="term-composer__form">
        <TerminalSendEmailForm
          ref={ref}
          initialData={initialData}
          resumeDraftId={resumeDraftId}
          priorityEmails={priorityEmails}
          onSendSuccess={() => {
            onSendSuccess({ subject: subjectForFlash, mode });
          }}
        />
      </div>

      <div className="term-composer__footer-rule" aria-hidden="true">───────────────────────────────────────────────</div>
    </div>
  );
};

export default TerminalComposer;
